import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { siteConfig } from './config';
import AboutPage from './AboutPage';
import GalleryPage from './GalleryPage';
import ReviewsPage from './ReviewsPage';
import ContactPage from './ContactPage';
import RoomsPage from './RoomsPage';
import WeatherWidget from './components/WeatherWidget';
import SEOHead from './components/SEOHead';

// Simple welcome page component
const WelcomePage = ({ isDarkMode }) => {
  return (
    <>
      <SEOHead 
        title="Home"
        description="J&B Lodge offers charming self-catering accommodation in Underberg, South Africa. Enjoy mountain views, fully equipped kitchen, and peaceful surroundings just 1km outside Underberg."
        keywords="J&B Lodge, Underberg accommodation, self-catering, South Africa, mountain views, Hodgson's Peaks, Sani Pass"
        url="/"
      />
      <div className={`min-h-screen font-sans ${isDarkMode ? 'bg-gray-900' : ''}`}>
      {/* Hero Section */}
      <div className="relative h-[450px] w-full overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('images/Sani Pass Hodgons Peak.webp')"
          }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          
          {/* Hero Content */}
          <div className="relative h-full flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight">
                Escape to {siteConfig.propertyArea}
              </h1>
              <p className="text-xl md:text-2xl font-medium opacity-90">
                Your perfect South African getaway awaits
              </p>
          </div>
            </div>
        </div>
          </div>

      {/* Main Content */}
      <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <main>
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
            {/* Left Column - 70% */}
            <div className="lg:col-span-7">
              <div className="text-left">
                <h1 className={`text-3xl md:text-4xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Overview of J&B Lodge
                </h1>
                <div className={`space-y-4 text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <p>
                    If you're looking for accommodation in Underberg, J&B Lodge has a charming self catering unit situated 1 km outside Underberg, on the Kokstad Road.
                  </p>
                  <p>
                    The unit has mountain views looking onto Hodgson's Peaks and Sani Pass. The unit has one bedroom, a private bathroom, a fully equipped kitchen and an open-plan living area with full HD DStv access. The lounge area leads out onto a sunny deck with lovely views.
                  </p>
                  <p>
                    There is undercover parking available with a separate entrance. Relax by the fireplace in the lounge area on a cold winter's night. Enjoy a relaxing drink on the deck while watching the sun fade behind the mountains.
                  </p>
                  <p>
                    The Underberg Country Club is only a short drive away for a round of golf. Regrettably no pets permitted.
                  </p>
                </div>

                {/* Weather Widget */}
                <div className="mt-6">
                  <WeatherWidget isDarkMode={isDarkMode} />
                </div>
              </div>
            </div>
            
            {/* Right Column - 30% */}
            <div className="lg:col-span-3">
              <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Quick Info
                </h3>
                <div className={`space-y-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <span>1 km outside Underberg, Kokstad Road</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"></path>
                    </svg>
                    <span>1 Bedroom, Private Bathroom</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
                    </svg>
                    <span>Mountain Views - Hodgson's Peaks</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                    <span>Fully Equipped Kitchen</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                    </svg>
                    <span>Full HD DStv Access</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <span>Regrettably no pets allowed</span>
                  </div>
                </div>
              </div>
              
              {/* Booking Rating Image */}
              <div className="mt-6">
                <img 
                  src="/images/Booking-rating.png" 
                  alt="Booking Rating" 
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
      </main>
        </div>

      {/* Gallery Section */}
      <div className="w-full" style={{ backgroundColor: '#f6f2ec' }}>
        <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-black text-center">
            J&B Lodge Gallery
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/homegallery/JB-Lodge-Sunset-300x225-1.jpg" 
                alt="J&B Lodge Sunset" 
                width="380"
                height="285"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/homegallery/JnB-Lodge-Living-Room-300x225-1.jpg" 
                alt="J&B Lodge Living Room" 
                width="380"
                height="285"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/homegallery/Outside-JB-Lodge-300x225-1.jpg" 
                alt="Outside J&B Lodge" 
                width="380"
                height="285"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/homegallery/JB-Lodge-Snow-300x225-1.jpg" 
                alt="J&B Lodge Snow" 
                width="380"
                height="285"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/homegallery/JB-Lodge-300x225-1.jpg" 
                alt="J&B Lodge" 
                width="380"
                height="285"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/homegallery/Inside-JB-Lodge-300x225-1.jpg" 
                alt="Inside J&B Lodge" 
                width="380"
                height="285"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Dark mode toggle function
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Apply dark mode classes to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Update document title
  useEffect(() => {
    document.title = siteConfig.tabTitle;
  }, []);

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Listen for booking modal events from other pages
  useEffect(() => {
    window.addEventListener('openBookingModal', handleBookingModal);
    return () => {
      window.removeEventListener('openBookingModal', handleBookingModal);
    };
  }, []);

  const handleBookingModal = (event) => {
    if (event.detail && event.detail.roomTypeId) {
      setSelectedRoomTypeId(event.detail.roomTypeId);
    }
    setShowBookingModal(true);
  };

  const handleBookingClick = () => {
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedRoomTypeId(null);
  };

  return (
    <div className={`min-h-screen font-sans ${isDarkMode ? 'dark bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b sticky top-0 z-30 py-2`}>
        <nav className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Mobile layout */}
          <div className="md:hidden flex items-center justify-between w-full">
            <div className="flex-grow flex justify-start">
              <button onClick={() => navigate('/')} className={`focus:outline-none rounded-lg cursor-pointer bg-transparent border-none p-0 text-3xl font-bold transition-colors duration-200 ${isDarkMode ? 'text-white hover:text-gray-300' : 'text-gray-800 hover:text-gray-900'}`}>
                {siteConfig.siteTitle}
              </button>
            </div>
            {/* Book Now Button - Mobile */}
            <button
              onClick={handleBookingClick}
              className={`px-4 py-2 rounded-full font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 mr-2 ${isDarkMode ? 'bg-[#2ecc71] text-white hover:bg-[#27ae60] focus:ring-[#2ecc71]' : 'bg-[#2ecc71] text-white hover:bg-[#27ae60] focus:ring-[#2ecc71]'}`}
            >
              {siteConfig.ctaButtonText}
            </button>
            <button
              className={`p-3 font-semibold focus:outline-none rounded-md z-10 transition-colors duration-200 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900'}`}
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              aria-label="Toggle mobile menu"
            >
              <svg className="w-7 h-7 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 6l12 12M6 18l12-12"></path>
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M8 6h16M4 12h16M6 18h16"></path>
                )}
              </svg>
            </button>
          </div>

          {/* Desktop layout */}
          <div className="hidden md:flex items-center justify-between w-full">
            <div className="flex items-center justify-start">
              <button onClick={() => navigate('/')} className={`focus:outline-none rounded-lg cursor-pointer bg-transparent border-none p-0 text-2xl font-bold transition-colors duration-200 ${isDarkMode ? 'text-white hover:text-gray-300' : 'text-gray-800 hover:text-gray-900'}`}>
                {siteConfig.siteTitle}
                </button>
            </div>

            <div className="flex items-center space-x-8 mx-auto">
              <button 
                onClick={() => navigate('/')}
                className={`block px-4 py-2 font-semibold transition-colors duration-200 bg-transparent border-none relative ${location.pathname === '/' ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')}`}
              >
                Home
                {location.pathname === '/' && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}></div>
                )}
              </button>
              <button 
                onClick={() => navigate('/rooms')}
                className={`block px-4 py-2 font-semibold transition-colors duration-200 bg-transparent border-none relative ${location.pathname === '/rooms' ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')}`}
              >
                Rooms
                {location.pathname === '/rooms' && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}></div>
                )}
            </button>
              <button 
                onClick={() => navigate('/gallery')}
                className={`block px-4 py-2 font-semibold transition-colors duration-200 bg-transparent border-none relative ${location.pathname === '/gallery' ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')}`}
              >
                Gallery
                {location.pathname === '/gallery' && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}></div>
                )}
              </button>
                <button
                onClick={() => navigate('/reviews')}
                className={`block px-4 py-2 font-semibold transition-colors duration-200 bg-transparent border-none relative ${location.pathname === '/reviews' ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')}`}
                >
                Reviews
                {location.pathname === '/reviews' && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}></div>
                )}
                </button>
              <button 
                onClick={() => navigate('/about')}
                className={`block px-4 py-2 font-semibold transition-colors duration-200 bg-transparent border-none relative ${location.pathname === '/about' ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')}`}
              >
                About
                {location.pathname === '/about' && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}></div>
                )}
              </button>
                <button
                onClick={() => navigate('/contact')}
                className={`block px-4 py-2 font-semibold transition-colors duration-200 bg-transparent border-none relative ${location.pathname === '/contact' ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')}`}
                >
                Contact
                {location.pathname === '/contact' && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}></div>
                )}
                </button>
              </div>

            <div className="flex items-center">
                <button
                onClick={handleBookingClick}
                className={`px-6 py-2 rounded-full font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'bg-[#2ecc71] text-white hover:bg-[#27ae60] focus:ring-[#2ecc71]' : 'bg-[#2ecc71] text-white hover:bg-[#27ae60] focus:ring-[#2ecc71]'}`}
                >
                {siteConfig.ctaButtonText}
                </button>
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className={`ml-4 p-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
                    </svg>
                  )}
                </button>
              </div>
              </div>
        </nav>

        {isMobileMenuOpen && (
          <div className={`md:hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg p-4 w-full absolute top-full left-0 z-20`} onClick={(e) => e.stopPropagation()}>
                      <button
              onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}
                              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${
                  location.pathname === '/' 
                    ? (isDarkMode ? 'text-white bg-gray-700' : 'text-gray-900') 
                    : (isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900')
                }`}
                style={location.pathname === '/' && !isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
                      >
              Home
                      </button>
                      <button
              onClick={() => { navigate('/rooms'); setIsMobileMenuOpen(false); }}
                              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${
                  location.pathname === '/rooms' 
                    ? (isDarkMode ? 'text-white bg-gray-700' : 'text-gray-900') 
                    : (isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900')
                }`}
                style={location.pathname === '/rooms' && !isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
                      >
              Rooms
                      </button>
                      <button
              onClick={() => { navigate('/gallery'); setIsMobileMenuOpen(false); }}
                              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${
                  location.pathname === '/gallery' 
                    ? (isDarkMode ? 'text-white bg-gray-700' : 'text-gray-900') 
                    : (isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900')
                }`}
                style={location.pathname === '/gallery' && !isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
                      >
              Gallery
                      </button>
                            <button
              onClick={() => { navigate('/reviews'); setIsMobileMenuOpen(false); }}
                              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${
                  location.pathname === '/reviews' 
                    ? (isDarkMode ? 'text-white bg-gray-700' : 'text-gray-900') 
                    : (isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900')
                }`}
                style={location.pathname === '/reviews' && !isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
            >
              Reviews
                            </button>
                      <button
              onClick={() => { navigate('/about'); setIsMobileMenuOpen(false); }}
                              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${
                  location.pathname === '/about' 
                    ? (isDarkMode ? 'text-white bg-gray-700' : 'text-gray-900') 
                    : (isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900')
                }`}
                style={location.pathname === '/about' && !isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
            >
              About
                      </button>
                                  <button
              onClick={() => { navigate('/contact'); setIsMobileMenuOpen(false); }}
              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${
                location.pathname === '/contact' 
                  ? (isDarkMode ? 'text-white bg-gray-700' : 'text-gray-900') 
                  : (isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900')
              }`}
              style={location.pathname === '/contact' && !isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
            >
              Contact
            </button>
            {/* Dark Mode Toggle - Mobile Menu */}
            <button
              onClick={toggleDarkMode}
              className={`block w-full px-4 py-3 font-semibold text-xl text-right border-none rounded transition-colors duration-200 ${isDarkMode ? 'text-gray-200 hover:text-white hover:bg-gray-700' : 'text-gray-800 hover:text-gray-900'}`}
              style={!isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}
            >
              {isDarkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </button>
            <button 
              onClick={handleBookingClick}
              className={`w-full text-center px-4 py-3 mt-2 rounded-md font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 text-xl ${isDarkMode ? 'bg-[#2ecc71] text-white hover:bg-[#27ae60] focus:ring-[#2ecc71]' : 'bg-[#2ecc71] text-white hover:bg-[#27ae60] focus:ring-[#2ecc71]'}`}
            >
              {siteConfig.ctaButtonText}
            </button>
                  </div>
                )}
      </header>

      <div className="w-full" onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}>
        <Routes>
          <Route path="/" element={<WelcomePage isDarkMode={isDarkMode} />} />
          <Route path="/rooms" element={<RoomsPage isDarkMode={isDarkMode} />} />
          <Route path="/about" element={<AboutPage isDarkMode={isDarkMode} />} />
          <Route path="/gallery" element={<GalleryPage isDarkMode={isDarkMode} />} />
          <Route path="/reviews" element={<ReviewsPage isDarkMode={isDarkMode} />} />
          <Route path="/contact" element={<ContactPage isDarkMode={isDarkMode} />} />
        </Routes>
              </div>

      {/* Footer */}
      <footer className={`text-white py-12 mt-auto ${isDarkMode ? 'bg-[#1f2937]' : ''}`} style={!isDarkMode ? { backgroundColor: '#312a1d' } : {}}>
        <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Column 1 - Facilities */}
                <div>
              <h3 className="text-lg font-semibold mb-4">Facilities</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Braai / Barbecue</li>
                <li>Fireplace on the Premises</li>
                <li>HD Satellite TV</li>
                <li>Wi-Fi</li>
                <li>ADSL</li>
                <li>Credit Card Facility</li>
                <li>Payfast</li>
                <li>English</li>
                <li>Afrikaans</li>
                <li>Self-catering</li>
              </ul>
              </div>

            {/* Column 2 - Activities */}
              <div>
              <h3 className="text-lg font-semibold mb-4">Activities</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Mountain Biking</li>
                <li>Fly Fishing</li>
                <li>Hiking</li>
                <li>Sani Pass Tours</li>
                <li>Golf</li>
                <li>Horse Riding</li>
                <li>Birding</li>
                <li>River Tubing</li>
                <li>Himeville Museum</li>
              </ul>
              </div>

            {/* Column 3 - Contact Details */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Details</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <p className="font-semibold">Barry Stevens</p>
                <p>J&B Lodge Self Catering</p>
                <p>4 Pin Oak Lane (Just off Ridge)</p>
                <p>Underberg,</p>
                <p>KwaZulu-Natal</p>
                <p>South Africa</p>
                <p>3257</p>
                <p className="mt-4">
                  <a href="tel:+27837944711" className="hover:text-white transition-colors duration-200">
                    +27 83 794 4711
                  </a>
                </p>
                <p>
                  <a href="mailto:jnblodge@gmail.com" className="hover:text-white transition-colors duration-200">
                    jnblodge@gmail.com
                  </a>
                </p>
                <p>
                  <a href="https://www.jnblodge.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">
                    www.jnblodge.co.za
                  </a>
                </p>
              </div>
            </div>

            {/* Column 4 - Gallery */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Gallery</h3>
              <div>
                <img 
                  src="/images/Footer Pics of JNB Lodge.png" 
                  alt="J&B Lodge Gallery" 
                  className="w-full object-cover rounded-lg"
                />
              </div>
                </div>
              </div>

                    {/* Footer bottom */}
          <div className="border-t mt-8 pt-8 text-center" style={{ borderColor: 'rgba(0,0,0,0.35)' }}>
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} J&B Lodge. All rights reserved. - Super fast website by <u><a href="https://www.stayhere.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">StayHere</a></u>.
            </p>
                </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-[90vw] h-[80vh] relative">
            {/* Close button */}
              <button
              onClick={closeBookingModal}
                              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                style={{ backgroundColor: '#f6f2ec' }}
              aria-label="Close booking modal"
              >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              </button>
            
            {/* Iframe container */}
            <div className="w-full h-full rounded-lg overflow-hidden">
              <iframe 
                id="nightsbridgeBookingFrame" 
                style={{ border: 'none' }} 
                src={selectedRoomTypeId 
                  ? `https://book.nightsbridge.com/${siteConfig.propertyBBID}?roomtypeid=${selectedRoomTypeId}`
                  : `https://book.nightsbridge.com/${siteConfig.propertyBBID}`
                } 
                width="100%" 
                height="100%"
                title="Book your stay"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Router wrapper
const AppWithRouter = () => (
  <Router>
    <SEOHead />
    <App />
  </Router>
);

export default AppWithRouter;